import React, { useRef, useState, useEffect, useCallback } from "react";
import styles from "styles.module.css";
import Filter from "./Filter";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { OverLayLoading } from "ui/Loading";
import { useTranslation } from "react-i18next";
import { useInfoStore } from "store/error";
import { salesNoteJournalGridColumns } from "./utils/GridColumns";
import { SalesNoteJournalFilter, getSalesNoteJournalApi } from "api/sales";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import SalesNoteJournalMobileList from "./SalesNoteJournalMobileList";
import { SalesNoteJournalItem } from "interface/sales";
import { PageTypePath } from "utils/pages";
import "./style.css";

const pageSize = 60;

const SalesNoteJournalPage = () => {
	const { t } = useTranslation();
	const overlayRef = useRef<HTMLDivElement>(null);
	const [gridData, setGridData] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const gridRef = useRef<HTMLDivElement>(null);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);
	const mobileNextSkip = useRef(0);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1700);
	const [mobileData, setMobileData] = useState<SalesNoteJournalItem[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);

	const [filterOptions, setFilterOptions] = useState<SalesNoteJournalFilter>({
		dateFrom: new Date(),
		dateTo: new Date(),
	});

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const { setError } = useInfoStore();

	useEffect(() => {
		if (isMobileView) return;
		setIsLoading(true);
		getSalesNoteJournalApi(filterOptions)
			.then((res) => {
				setGridData(res ?? []);
			})
			.catch((err) => {
				setError(err);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [filterOptions, isMobileView]);

	const requestMobileData = useCallback(
		(skip: number, take: number, resetData = false) => {
			if (mobileRequestInProgress.current || mobileDataFinished.current) {
				return;
			}

			mobileRequestInProgress.current = true;
			setMobileLoading(true);

			getSalesNoteJournalApi(filterOptions)
				.then((incoming) => {
					const data = (incoming || []) as SalesNoteJournalItem[];

					if (data.length === 0) {
						mobileDataFinished.current = true;
						if (resetData) {
							setMobileData([]);
						}
						return;
					}

					mobileNextSkip.current = skip + take;

					if (resetData) {
						setMobileData(data);
					} else {
						setMobileData((prev) => [...prev, ...data]);
					}

					mobileDataFinished.current = true;
				})
				.finally(() => {
					mobileRequestInProgress.current = false;
					setMobileLoading(false);
				});
		},
		[filterOptions],
	);

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 1600);
		window.addEventListener("resize", updateMobileView);
		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) return;

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);

		requestMobileData(0, pageSize, true);
	}, [isMobileView, requestMobileData]);

	const exportGridAsPDF = () => {
		localStorage.setItem(
			"fromDate",
			filterOptions.dateFrom instanceof Date
				? filterOptions.dateFrom.toISOString()
				: "",
		);
		localStorage.setItem(
			"toDate",
			filterOptions.dateTo instanceof Date
				? filterOptions.dateTo.toISOString()
				: "",
		);
		localStorage.setItem(
			"byerLedgerAccount",
			filterOptions.byerLedgerAccount?.toString() ?? "",
		);
		localStorage.setItem("terminal", filterOptions.terminal ?? "");
		localStorage.setItem("species", filterOptions.species ?? "");
		localStorage.setItem(
			"fishingGearCode",
			filterOptions.fishingGearCode ?? "",
		);
		localStorage.setItem("catchField1", filterOptions.catchField1 ?? "");
		localStorage.setItem("catchField2", filterOptions.catchField2 ?? "");
		localStorage.setItem("conditionCode", filterOptions.conditionCode ?? "");
		localStorage.setItem(
			"preservationCode",
			filterOptions.preservationCode ?? "",
		);
		localStorage.setItem(
			"fishermanLedgerAccount",
			filterOptions.fishermanLedgerAccount ?? "",
		);
		localStorage.setItem("orderBy", filterOptions.orderBy ?? "");

		window.open(`/${PageTypePath.Pdf}/0/6/`, "_blank");
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	return (
		<div className={`${styles.PageContainer} sales-note-journal-page`}>
			<div ref={overlayRef}>
				<OverLayLoading show={isLoading} />
			</div>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("SalesNoteJournal")}
				</legend>
			</div>
			<div>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
				/>
			</div>
			<div ref={gridRef}>
				{isMobileView ? (
					<SalesNoteJournalMobileList
						items={mobileData}
						loading={mobileLoading}
						onLoadMore={() =>
							requestMobileData(mobileNextSkip.current, pageSize)
						}
					/>
				) : (
					<GridWithGroupPaging
						gridColumns={salesNoteJournalGridColumns(gridData)}
						gridData={gridData}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						pageable={false}
						sortable={true}
						filterable={false}
						excelProps={{
							fileName: `sales_note_journal_${new Date().toISOString()}.xlsx`,
						}}
					/>
				)}
			</div>
		</div>
	);
};

export default SalesNoteJournalPage;
